import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ChefHat, Loader2, Lock, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function formatDetail(detail) {
  if (!detail) return "Something went wrong. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = (params.get("token") || "").trim();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Missing reset token. Request a fresh link.");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated. Signed in.");
      // Refresh the auth context so the app picks up the new session cookie.
      if (typeof refresh === "function") {
        await refresh();
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Reset failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="reset-password-page">
      <div className="relative hidden md:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1600&q=80"
          alt="Restaurant pass"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/50 to-black/70" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-foreground">
          <div className="label-eyebrow gold mb-4">New password</div>
          <h1 className="font-display text-5xl md:text-6xl leading-[1.02] mb-4 tracking-tight">
            Fresh key.<br /><span className="gold">Same kitchen.</span>
          </h1>
          <p className="text-foreground/70 max-w-md text-sm">
            Set a new password. The moment you save it, we sign you back in.
          </p>
        </div>
      </div>

      <div className="relative flex items-center justify-center p-6 md:p-12">
        <div className="glass w-full max-w-sm rounded-[28px] p-8 md:p-10" data-testid="reset-glass-panel">
          <div className="flex items-center gap-3 mb-10">
            <img
              src="/brand/mark-256.png"
              alt="CulinaireSpecialist"
              className="w-10 h-10 rounded-full object-contain"
              style={{ background: "#0a0806", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15), 0 0 0 1px rgba(232,207,154,0.35), 0 4px 12px rgba(200,169,106,0.22)" }}
            />
            <div>
              <div className="font-display text-xl leading-tight text-white/95">CulinaireSpecialist</div>
              <div className="text-[0.55rem] uppercase tracking-[0.28em] mt-0.5" style={{ color: "rgba(232,207,154,0.85)" }}>
                Set new password
              </div>
            </div>
          </div>

          {!token ? (
            <div data-testid="reset-missing-token">
              <h2 className="font-display text-2xl text-white/95 mb-3">Missing reset link.</h2>
              <p className="text-sm text-white/60 mb-6 leading-relaxed">
                This page needs a token from the email we sent. Request a fresh link and try again.
              </p>
              <Link
                to="/forgot-password"
                className="gold-cta group w-full h-11 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold"
                data-testid="reset-request-new"
              >
                Request a new link
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-display text-3xl text-white/95 mb-2">Choose a new password.</h2>
              <p className="text-sm text-white/55 mb-8">At least 6 characters. Mix in a symbol for good measure.</p>

              <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                <div className="space-y-1.5">
                  <label htmlFor="new-password" className="block text-xs text-white/70">New password</label>
                  <div className="relative">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/45 pointer-events-none" />
                    <input
                      id="new-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="glass-input w-full h-10 pl-9 pr-3 rounded-lg text-sm text-white/95"
                      data-testid="reset-new-password"
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="confirm-password" className="block text-xs text-white/70">Confirm password</label>
                  <div className="relative">
                    <ShieldCheck className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/45 pointer-events-none" />
                    <input
                      id="confirm-password"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      minLength={6}
                      className="glass-input w-full h-10 pl-9 pr-3 rounded-lg text-sm text-white/95"
                      data-testid="reset-confirm-password"
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="gold-cta group w-full h-11 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold disabled:opacity-60"
                  data-testid="reset-submit"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Save and sign in
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>
              </form>

              <p className="text-sm text-white/50 mt-6">
                Remembered your password?{" "}
                <Link
                  to="/login"
                  className="font-medium hover:brightness-110 transition-all"
                  style={{ color: "#f2dfb5" }}
                  data-testid="reset-link-login"
                >
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
